import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarProvider,
  SidebarTrigger,
  SidebarInset,
  useSidebar,
} from '../../../components/ui/sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import NavigationActions from './NavigationActions';
import BottomActions from './BottomActions';
import BottomActionsModals from './BottomActionsModals';
import { setDrawerState } from '../dashboardSlice';
import FolderTree from './FolderTree';
import FilesList from './FilesList';
import { RootState } from '../../../reducers';
import { getFolderUrl } from '../../../lib/routingUtils';
import { useTeamTags, useUserTags } from '../../../hooks/useTags';
import { getTeamLogoUrl } from '../../../lib/teamLogoUtils';
import ChatPanel from '../../chat/ChatPanel';

const CustomSidebar = ({ sidebarWidth }: { sidebarWidth?: number }) => {
  const { t } = useTranslation();
  const { team } = useSelector((state: RootState) => ({
    team: state.team,
  }));
  const { getTeamTag } = useTeamTags();
  const { getTag } = useUserTags();

  // Get team logo with proper fallback chain: pictureUrl -> team_logo_url -> default
  const teamLogoUrl = getTeamLogoUrl(
    team.selectedTeam?.pictureUrl,
    getTeamTag('team_logo_url'),
  );

  // Check if files on sidebar is enabled
  const filesOnSidebarTag = getTag('files-on-sidebar');
  const showFilesOnSidebar = filesOnSidebarTag === 'true';

  // Get isMobile and open state from context
  const { isMobile, open } = useSidebar();
  const [showBottomSeparator, setShowBottomSeparator] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const getRecentsUrl = () => {
    const teamId = team.selectedTeam?.teamId || team.teams[0]?.teamId;
    return getFolderUrl({
      teamId,
      folderId: 'recents',
      withBasePath: false,
    });
  };

  // Handle scroll detection for separator
  React.useEffect(() => {
    const checkScroll = () => {
      if (contentRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
        const isAtBottom =
          Math.abs(scrollHeight - clientHeight - scrollTop) < 1;
        setShowBottomSeparator(!isAtBottom && scrollHeight > clientHeight);
      }
    };

    const scrollElement = contentRef.current;
    if (scrollElement) {
      checkScroll(); // Initial check
      scrollElement.addEventListener('scroll', checkScroll);

      // Also check on resize
      const resizeObserver = new ResizeObserver(checkScroll);
      resizeObserver.observe(scrollElement);

      return () => {
        scrollElement.removeEventListener('scroll', checkScroll);
        resizeObserver.disconnect();
      };
    }

    return undefined;
  }, []);

  return (
    <TooltipProvider delayDuration={0}>
      <Sidebar
        collapsible={isMobile ? 'offcanvas' : 'icon'}
        className="bg-white dark:bg-secondary border-r border-border [&_[data-sidebar=sidebar]]:bg-white dark:[&_[data-sidebar=sidebar]]:bg-secondary h-full"
        style={
          {
            '--sidebar-width': sidebarWidth ? `${sidebarWidth}px` : undefined,
          } as React.CSSProperties
        }
      >
        <SidebarHeader className="h-16 flex items-center px-3 pt-3">
          <div className="flex items-center justify-between w-full">
            {isMobile ? (
              // Mobile: show hamburger menu and logo
              <>
                <Link
                  to={getRecentsUrl()}
                  className="flex items-center shrink-0"
                >
                  <img
                    src={teamLogoUrl}
                    alt={t('drawer.logo.teamLogo')}
                    className="object-contain h-8 w-auto max-w-[120px]"
                  />
                </Link>
                <SidebarTrigger
                  icon="menu"
                  className="h-8 w-8 rounded-md hover:bg-accent dark:hover:bg-accent [&_svg]:stroke-[1.75] text-gray-400 dark:text-gray-500 [&_svg]:text-gray-400 dark:[&_svg]:text-gray-500"
                />
              </>
            ) : (
              // Desktop: show logo (clickable to expand when collapsed, navigates when open)
              <div className="flex items-center justify-between w-full">
                {open ? (
                  // When open: logo navigates, show collapse button
                  <>
                    <Link
                      to={getRecentsUrl()}
                      className="flex items-center shrink-0"
                    >
                      <img
                        src={teamLogoUrl}
                        alt={t('drawer.logo.teamLogo')}
                        className="object-contain h-8 w-8"
                      />
                    </Link>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <SidebarTrigger className="h-8 w-8 rounded-md hover:bg-accent dark:hover:bg-accent [&_svg]:stroke-[1.75] cursor-w-resize text-gray-400 dark:text-gray-500 hover:text-foreground [&_svg]:text-gray-400 dark:[&_svg]:text-gray-500 hover:[&_svg]:text-foreground" />
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        <p>Collapse sidebar</p>
                      </TooltipContent>
                    </Tooltip>
                  </>
                ) : (
                  // When collapsed: logo expands sidebar
                  <div className="flex items-center shrink-0">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <SidebarTrigger className="h-8 w-8 p-0 border-0 bg-transparent hover:bg-transparent dark:hover:bg-transparent focus:outline-none cursor-e-resize flex items-center justify-center">
                          <img
                            src={teamLogoUrl}
                            alt={t('drawer.logo.teamLogo')}
                            className="object-contain h-8 w-8"
                          />
                        </SidebarTrigger>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <p>Expand sidebar</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                )}
              </div>
            )}
          </div>
        </SidebarHeader>
        <SidebarContent ref={contentRef}>
          <NavigationActions />
          {showFilesOnSidebar ? (
            <FilesList />
          ) : (
            <div className="px-2">
              <FolderTree />
            </div>
          )}
        </SidebarContent>
        <SidebarFooter className="p-0 relative">
          <div
            className={`absolute top-0 left-0 right-0 h-px bg-border transition-opacity duration-200 ${
              showBottomSeparator ? 'opacity-100' : 'opacity-0'
            }`}
          />
          <BottomActions />
        </SidebarFooter>
      </Sidebar>
    </TooltipProvider>
  );
};

// Wrapper component that provides the SidebarProvider context
const SidebarWrapper = ({ children }: { children?: React.ReactNode }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { getTag, setTag } = useUserTags();
  const [isInitialized, setIsInitialized] = React.useState(false);
  const prevIsMobileRef = React.useRef<boolean | null>(null);
  const [sidebarWidth, setSidebarWidth] = React.useState(() => {
    const savedWidth = getTag('sidebar-width');
    return savedWidth ? parseFloat(savedWidth) : 256; // Default 256px width (16rem)
  });
  const [isDragging, setIsDragging] = React.useState(false);
  const dragStateRef = React.useRef({
    startX: 0,
    initialWidth: 256,
    currentWidth: 256,
  });
  const animationFrameRef = React.useRef<number | null>(null);

  // Track if the chat panel has ever been manually toggled (to enable animations)
  const [chatHasBeenToggled, setChatHasBeenToggled] = React.useState(false);

  // Mobile-specific chat panel state (separate from desktop preferences)
  const [mobileChatPanelOpen, setMobileChatPanelOpen] = React.useState(false);

  // Get files from library state to check if there's more than one file
  const { files } = useSelector((state: RootState) => state.library);

  // Compute if chat should be open based on path
  const pathParts = location.pathname.split('/').filter(Boolean);
  // Selected video view: /library/{teamId}/{folderId}/{fileId} (4 parts)
  // Library list (multi-ask AI): /library/{teamId}/recents (3 parts)
  const isSelectedVideoView =
    pathParts[0] === 'library' && pathParts.length === 4;
  const isLibraryView = pathParts[0] === 'library' && pathParts.length === 3;
  const hasFiles =
    files?.data &&
    files.data.filter((f) => f._id !== 'demo-video-onboarding').length >= 1;

  // Check if mobile
  const isMobileViewport =
    typeof window !== 'undefined' && window.innerWidth < 768;

  // Get saved library chat panel preference (null means no preference saved yet - new user)
  const savedLibraryChatPanelOpen = getTag('library-chat-panel-open');

  // Get saved video chat panel preference
  const savedVideoChatPanelOpen = getTag('video-chat-panel-open');

  // Determine chat panel state based on view type
  // For library view: use saved preference immediately, default to open for new users (once files load)
  // For video view: use saved preference, default to open
  // For mobile: use mobileChatPanelOpen state (always starts closed, user must explicitly open)
  const chatPanelOpen = React.useMemo(() => {
    // On mobile, use the mobile-specific state
    if (isMobileViewport) return mobileChatPanelOpen;

    if (isSelectedVideoView) {
      // Use saved preference, default to open for first-time viewing
      if (savedVideoChatPanelOpen !== null) {
        return savedVideoChatPanelOpen === 'true';
      }
      return true; // Default open for first time
    }

    if (isLibraryView) {
      // If user has a saved preference, use it immediately (don't wait for files to load)
      if (savedLibraryChatPanelOpen !== null) {
        return savedLibraryChatPanelOpen === 'true';
      }
      // For new users (no saved preference), open by default once files are loaded
      return hasFiles;
    }

    return false;
  }, [
    isMobileViewport,
    mobileChatPanelOpen,
    isSelectedVideoView,
    isLibraryView,
    hasFiles,
    savedLibraryChatPanelOpen,
    savedVideoChatPanelOpen,
  ]);
  const [chatPanelWidth, setChatPanelWidth] = React.useState(() => {
    const savedWidth = getTag('chat-panel-width');
    return savedWidth ? parseFloat(savedWidth) : 400;
  });

  // Track chat panel drag width for real-time margin updates
  const [chatDragWidth, setChatDragWidth] = React.useState<number | null>(null);

  // Determine initial state based on saved preference
  const getInitialState = () => {
    if (typeof window === 'undefined') return 'open';
    if (window.innerWidth < 768) return 'closed';
    const savedState = getTag('sidebar-collapsed');
    return savedState === 'true' ? 'closed' : 'open';
  };

  const [sidebarOpen, setSidebarOpen] = React.useState(
    () => getInitialState() === 'open',
  );

  // Track the current drag width for real-time updates
  const [currentDragWidth, setCurrentDragWidth] = React.useState<number | null>(
    null,
  );

  // Store refs to avoid stale closures
  const dispatchRef = React.useRef(dispatch);
  const getTagRef = React.useRef(getTag);
  const setTagRef = React.useRef(setTag);

  React.useEffect(() => {
    dispatchRef.current = dispatch;
    getTagRef.current = getTag;
    setTagRef.current = setTag;
  });

  // Handle chat panel width changes
  const handleChatPanelWidthChange = React.useCallback((width: number) => {
    setChatPanelWidth(width);
    setTagRef.current({
      key: 'chat-panel-width',
      value: width.toString(),
    });
  }, []);

  // Handle chat panel drag width changes (real-time updates during drag)
  const handleChatDragWidthChange = React.useCallback(
    (width: number | null) => {
      setChatDragWidth(width);
    },
    [],
  );

  // Expose chat panel controls to NavigationActions
  React.useEffect(() => {
    (window as any).__chatPanelControls = {
      open: () => {
        setChatHasBeenToggled(true);
        // On mobile, use mobile state
        if (isMobileViewport) {
          setMobileChatPanelOpen(true);
          return;
        }
        // Save preference for library view
        if (isLibraryView) {
          setTag({ key: 'library-chat-panel-open', value: 'true' });
        }
        // Save preference for video view
        if (isSelectedVideoView) {
          setTag({ key: 'video-chat-panel-open', value: 'true' });
        }
      },
      close: () => {
        setChatHasBeenToggled(true);
        // On mobile, use mobile state
        if (isMobileViewport) {
          setMobileChatPanelOpen(false);
          return;
        }
        // Save preference for library view
        if (isLibraryView) {
          setTag({ key: 'library-chat-panel-open', value: 'false' });
        }
        // Save preference for video view
        if (isSelectedVideoView) {
          setTag({ key: 'video-chat-panel-open', value: 'false' });
        }
      },
      toggle: () => {
        setChatHasBeenToggled(true);
        // On mobile, use mobile state
        if (isMobileViewport) {
          setMobileChatPanelOpen((prev) => !prev);
          return;
        }
        // Save preference for library view
        if (isLibraryView) {
          setTag({
            key: 'library-chat-panel-open',
            value: chatPanelOpen ? 'false' : 'true',
          });
        }
        // Save preference for video view
        if (isSelectedVideoView) {
          setTag({
            key: 'video-chat-panel-open',
            value: chatPanelOpen ? 'false' : 'true',
          });
        }
      },
      isOpen: () => chatPanelOpen,
    };

    return () => {
      delete (window as any).__chatPanelControls;
    };
  }, [
    chatPanelOpen,
    isMobileViewport,
    isLibraryView,
    isSelectedVideoView,
    setTag,
  ]);

  // Initialize and handle resize - run only once on mount
  React.useEffect(() => {
    const handleResize = () => {
      const nowMobile = window.innerWidth < 768;
      const wasMobile = prevIsMobileRef.current;

      // Only handle transitions after initialization
      if (wasMobile !== null && wasMobile !== nowMobile) {
        if (nowMobile) {
          // Going to mobile - always close
          setSidebarOpen(false);
          dispatchRef.current(setDrawerState('closed'));
        } else {
          // Going to desktop - check saved preference
          const savedState = getTagRef.current('sidebar-collapsed');
          const shouldOpen = savedState !== 'true';
          setSidebarOpen(shouldOpen);
          dispatchRef.current(setDrawerState(shouldOpen ? 'open' : 'closed'));
        }
      }

      prevIsMobileRef.current = nowMobile;
    };

    // Initial setup
    const nowMobile = window.innerWidth < 768;
    prevIsMobileRef.current = nowMobile;
    dispatchRef.current(
      setDrawerState(getInitialState() === 'open' ? 'open' : 'closed'),
    );

    // Enable animations after mount
    setIsInitialized(true);

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []); // Empty dependency array - run only once

  const handleOpenChange = React.useCallback(
    (openState: boolean) => {
      setSidebarOpen(openState);
      dispatchRef.current(setDrawerState(openState ? 'open' : 'closed'));
      // Only save state for desktop after initialization
      const isMobile = window.innerWidth < 768;
      if (!isMobile && isInitialized) {
        setTagRef.current({
          key: 'sidebar-collapsed',
          value: openState ? 'false' : 'true',
        });
      }
    },
    [isInitialized],
  );

  // Handle resize drag start
  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // If sidebar is collapsed, we're starting from the icon width
    // Otherwise use the current sidebar width
    const initialWidth = sidebarOpen ? sidebarWidth : 56;

    dragStateRef.current = {
      startX: e.clientX,
      initialWidth,
      currentWidth: initialWidth,
    };

    setIsDragging(true);

    // Add class to disable transitions during drag
    document.documentElement.classList.add('sidebar-dragging');
  };

  // Store sidebarOpen in a ref to avoid stale closure issues
  const sidebarOpenRef = React.useRef(sidebarOpen);
  React.useEffect(() => {
    sidebarOpenRef.current = sidebarOpen;
  }, [sidebarOpen]);

  // Handle resize mouse move - no dependencies to avoid stale closures
  const handleMouseMove = React.useCallback((e: MouseEvent) => {
    // Cancel any pending animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    // Use requestAnimationFrame for smooth updates
    animationFrameRef.current = requestAnimationFrame(() => {
      const dragDistance = e.clientX - dragStateRef.current.startX;
      const rawWidth = dragStateRef.current.initialWidth + dragDistance;

      const SNAP_THRESHOLD = 120;
      const EXPAND_THRESHOLD = 120;

      // If currently collapsed and dragged right past threshold, trigger expand
      if (!sidebarOpenRef.current && rawWidth > EXPAND_THRESHOLD) {
        // Immediately trigger expand (same as clicking expand icon)
        setIsDragging(false);
        setCurrentDragWidth(null);
        document.documentElement.classList.remove('sidebar-dragging');
        document.documentElement.style.removeProperty('--sidebar-resize-width');

        setSidebarOpen(true);
        dispatchRef.current(setDrawerState('open'));
        setTagRef.current({
          key: 'sidebar-collapsed',
          value: 'false',
        });
        return;
      }

      // If currently open and dragged left below threshold, trigger collapse
      if (sidebarOpenRef.current && rawWidth < SNAP_THRESHOLD) {
        // Immediately trigger collapse (same as clicking collapse icon)
        setIsDragging(false);
        setCurrentDragWidth(null);
        document.documentElement.classList.remove('sidebar-dragging');
        document.documentElement.style.removeProperty('--sidebar-resize-width');

        setSidebarOpen(false);
        dispatchRef.current(setDrawerState('closed'));
        setTagRef.current({
          key: 'sidebar-collapsed',
          value: 'true',
        });
        return;
      }

      // Only update visual width if sidebar is open
      if (sidebarOpenRef.current) {
        // Keep visual width at minimum 200px, maximum 480px
        const newWidth = Math.max(200, Math.min(480, rawWidth));

        // Store current width in ref
        dragStateRef.current.currentWidth = newWidth;

        // Update CSS variable directly for instant feedback
        document.documentElement.style.setProperty(
          '--sidebar-resize-width',
          `${newWidth}px`,
        );

        // Update state for real-time rendering (batched by React)
        setCurrentDragWidth(newWidth);
      }
    });
  }, []); // Empty deps - use refs for all values

  // Handle resize mouse up - use ref to avoid dependencies
  const isDraggingRef = React.useRef(isDragging);
  React.useEffect(() => {
    isDraggingRef.current = isDragging;
  }, [isDragging]);

  const handleMouseUp = React.useCallback(() => {
    if (!isDraggingRef.current) return;

    // Get the final width from our ref (more reliable than CSS variable)
    const finalWidth = dragStateRef.current.currentWidth;

    setIsDragging(false);
    setCurrentDragWidth(null); // Clear drag width state

    // Remove the transition-disabling class
    document.documentElement.classList.remove('sidebar-dragging');

    // Clean up CSS variable
    document.documentElement.style.removeProperty('--sidebar-resize-width');

    // Cancel any pending animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Enforce minimum width of 200px and save
    const constrainedWidth = Math.max(200, finalWidth);
    setSidebarWidth(constrainedWidth);

    // Save the constrained width
    setTagRef.current({
      key: 'sidebar-width',
      value: constrainedWidth.toString(),
    });
  }, []); // Empty deps - use refs for all values

  // Add/remove mouse event listeners when dragging
  React.useEffect(() => {
    if (isDragging) {
      // Use capture phase for immediate response
      window.addEventListener('mousemove', handleMouseMove, { capture: true });
      window.addEventListener('mouseup', handleMouseUp, { capture: true });

      // Prevent text selection and set cursor
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
      (document.body.style as any).webkitUserSelect = 'none';
      document.body.style.pointerEvents = 'none';

      // Keep the resize handle interactive
      const handle = document.querySelector(
        '[data-testid="sidebar-resize-handle"]',
      ) as HTMLElement;
      if (handle) {
        handle.style.pointerEvents = 'auto';
      }
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove, {
        capture: true,
      });
      window.removeEventListener('mouseup', handleMouseUp, { capture: true });
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      (document.body.style as any).webkitUserSelect = '';
      document.body.style.pointerEvents = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Get effective width (drag width during drag, otherwise saved width)
  const effectiveWidth = currentDragWidth ?? sidebarWidth;

  // Use CSS variable value during drag, otherwise use state
  const currentWidth = isDragging
    ? `var(--sidebar-resize-width, ${sidebarWidth}px)`
    : `${sidebarWidth}px`;

  return (
    <>
      <style>
        {/* eslint-disable-next-line no-useless-escape */}
        {`
          .sidebar-dragging * {
            transition: none !important;
          }
          .sidebar-dragging [data-sidebar="sidebar"] {
            transition: none !important;
          }
          .sidebar-dragging .group\\/sidebar-wrapper > div:first-child > div:first-child {
            transition: none !important;
          }
          .chat-panel-dragging * {
            transition: none !important;
          }
        `}
      </style>
      <SidebarProvider
        defaultOpen={sidebarOpen}
        open={sidebarOpen}
        onOpenChange={handleOpenChange}
        style={
          {
            '--sidebar-width':
              !isMobile && sidebarOpen ? currentWidth : undefined,
          } as React.CSSProperties
        }
      >
        <div
          className={`flex h-screen w-full ${!isInitialized ? 'sidebar-no-transition' : ''}`}
        >
          <CustomSidebar
            sidebarWidth={!isMobile && sidebarOpen ? effectiveWidth : undefined}
          />
          {/* Resize handle - show on desktop both when open and collapsed */}
          {!isMobile && (
            <button
              type="button"
              className="fixed top-0 w-4 h-full cursor-ew-resize group z-50 border-0 bg-transparent p-0 outline-none"
              style={{
                left: sidebarOpen ? `${effectiveWidth - 2}px` : '54px', // 56px - 2px for collapsed state
              }}
              onMouseDown={handleResizeMouseDown}
              aria-label="Resize sidebar"
              data-testid="sidebar-resize-handle"
              onKeyDown={(e) => {
                // Allow keyboard resizing with arrow keys
                if (e.key === 'ArrowLeft') {
                  e.preventDefault();
                  const newWidth = Math.max(200, sidebarWidth - 10);
                  setSidebarWidth(newWidth);
                  setTagRef.current({
                    key: 'sidebar-width',
                    value: newWidth.toString(),
                  });
                } else if (e.key === 'ArrowRight') {
                  e.preventDefault();
                  const newWidth = Math.min(480, sidebarWidth + 10);
                  setSidebarWidth(newWidth);
                  setTagRef.current({
                    key: 'sidebar-width',
                    value: newWidth.toString(),
                  });
                }
              }}
            >
              <div
                className={cn(
                  'absolute inset-y-0 left-0 w-1 transition-all',
                  isDragging ? 'bg-blue-400 opacity-100' : 'opacity-0',
                )}
              />
            </button>
          )}
          <SidebarInset
            className={cn(
              'flex-1 flex flex-col overflow-hidden min-w-[33vw]',
              chatHasBeenToggled &&
                !chatDragWidth &&
                'transition-[margin-right] duration-200 ease-in-out',
            )}
            style={{
              marginRight:
                chatPanelOpen && !isMobile
                  ? `${chatDragWidth ?? chatPanelWidth}px`
                  : '0px',
            }}
          >
            {children}
          </SidebarInset>
          {/* Chat Panel */}
          <ChatPanel
            open={chatPanelOpen}
            onClose={() => {
              setChatHasBeenToggled(true);
              // On mobile, use mobile state
              if (isMobileViewport) {
                setMobileChatPanelOpen(false);
                return;
              }
              // Save preference for library view
              if (isLibraryView) {
                setTag({ key: 'library-chat-panel-open', value: 'false' });
              }
              // Save preference for video view
              if (isSelectedVideoView) {
                setTag({ key: 'video-chat-panel-open', value: 'false' });
              }
            }}
            width={chatPanelWidth}
            onWidthChange={handleChatPanelWidthChange}
            onDragWidthChange={handleChatDragWidthChange}
            shouldAnimate={chatHasBeenToggled}
          />
        </div>
        {/* Render modals outside of sidebar so they persist when sidebar closes */}
        <BottomActionsModals />
      </SidebarProvider>
    </>
  );
};

export default SidebarWrapper;
